import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { FlexRow } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { formatPrice, prop, ZemplinLoaderWrapper } from '../../..';
import { TableWrapper } from '../General/TableWrapper';
import { TableRow, SecondTableTh, TableThSubHeader } from '../General/Table';
import { DsiBackorderRow as BackorderRow } from './BackorderRow';
import { Filters } from './Invoices';
import { Select } from '../../Zemplin/General/Select';

interface Props {
  orders: any;
  isFetching: boolean;
  currency: string;
  sumPrice?: number;
  brands?: any;
  categories?: any;
  refetchData?: any;
  user: any;
}

function Backorder({
  orders,
  isFetching,
  currency,
  sumPrice,
  brands,
  categories,
  refetchData,
  user,
}: Props) {
  const [brandState, setBrandState] = React.useState('');
  const [categoryState, setCategoryState] = React.useState('');
  const [onlyReserved, setOnlyReserved] = React.useState('');
  const columns = [
    { name: __('Produkt') },
    { name: __('Č. objednávky') },
    { name: __('Dátum objednávky') },
    {
      name: __('Množstvo'),
      secondLine: __('Objednané / Rezervované'),
      align: 'center',
    },
    {
      name: __('Celkové množstvo'),
      secondLine: __('Objednané / Rezervované'),
      align: 'center',
    },
    { name: __('Suma'), align: 'right' },
  ];
  const footerColumns = [
    { name: __('Spolu') },
    { name: '' },
    { name: '' },
    { name: '' },
    { name: '' },
    { name: formatPrice(sumPrice || 0, currency), align: 'right' },
  ];
  const headers =
    columns &&
    columns.map((item: any) => (
      <SecondTableTh
        width={item.width}
        minWidth={item.minWidth}
        align={item.align || 'left'}
        key={item.name}
      >
        {item.name}
        <TableThSubHeader>
          {item.secondLine && item.secondLine}
        </TableThSubHeader>
      </SecondTableTh>
    ));

  const footers =
    footerColumns &&
    footerColumns.map((item: any) => (
      <SecondTableTh
        width={item.width}
        minWidth={item.minWidth}
        align={item.align || 'left'}
        key={item.name}
      >
        {item.name}
      </SecondTableTh>
    ));

  const orderRows =
    orders &&
    orders.map(o => (
      <BackorderRow
        user={user}
        currency={currency}
        key={o.product.product_id}
        order={o}
      />
    ));

  const onChangeBrand = e => {
    const {
      target: { value },
    } = e;
    setBrandState(value);
    refetchData({
      brandId: value,
      categId: categoryState,
      reserved: onlyReserved,
    });
  };

  const onChangeCategory = e => {
    const {
      target: { value },
    } = e;
    setCategoryState(value);
    refetchData({
      brandId: brandState,
      categId: value,
      reserved: onlyReserved,
    });
  };

  const onChangeOnlyReserved = e => {
    const {
      target: { value },
    } = e;
    setOnlyReserved(value);
    refetchData({
      brandId: brandState,
      categId: categoryState,
      reserved: value,
    });
  };

  return (
    <Wrapper>
      <Filters>
        {brands && brands.length > 0 && (
          <Select
            value={brandState}
            options={[
              { name: '', value: '' },
              ...brands.map(b => ({
                name: prop(b, 'translations.0.brand_name') || b.brand_name,
                value: b.brand_id,
              })),
            ]}
            onChange={onChangeBrand}
            maxWidth={240}
            backgroundColor={'#FFFFFF'}
            marginRight={14}
            placeholder={__('Vyberte výrobcu')}
          />
        )}
        {categories && categories.length > 0 && (
          <Select
            value={categoryState}
            options={[
              { name: '', value: '' },
              ...categories.map(c => ({
                name: c.category_name,
                value: c.category_id,
              })),
            ]}
            onChange={onChangeCategory}
            maxWidth={240}
            backgroundColor={'#FFFFFF'}
            marginRight={14}
            placeholder={__('Vyberte kategóriu')}
          />
        )}

        <Select
          value={onlyReserved}
          options={[
            { name: __('Všetky'), value: '' },
            {
              name: __('Iba rezervované'),
              value: '1',
            },
          ]}
          onChange={onChangeOnlyReserved}
          maxWidth={240}
          backgroundColor={'#FFFFFF'}
          marginRight={14}
          placeholder={__('Vyberte stav rezervácie')}
        />
      </Filters>
      {isFetching ? (
        <ZemplinLoaderWrapper height={500} />
      ) : (
        <TableWrapper
          tableHasBorder={true}
          tableHasBoxShadow={true}
          noItemsText={__('Nenašli sa žiadne backorders')}
          headers={headers}
          footers={footers}
          rows={orderRows}
          overflowX={true}
        />
      )}
    </Wrapper>
  );
}

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexRow)`
  flex-flow: row wrap;
  margin-bottom: ${rem(24)};
  animation: ${animateIn} 0.2s ease;
  width: 100%;

  ${({ theme }) => theme.mediab.m810`
      margin: 0;
      margin-bottom: ${({ visible }) => (visible ? rem(32) : 0)};
      margin-right: -${rem(16)};
  `};
`;

export { Backorder as DsiBackorder };
